import React from 'react';
import styles from './CreditReport.module.scss';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox/HeaderBodyBox';
import clsx from 'clsx';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import AIGeneratedBadge from '../../../../../../common/AIGeneratedBadge/AIGeneratedBadge';
import TopRightArrowIcon from '../../../../../../assets/Svg/TopRightArrowIcon';
import { headers, HEADER_IDS, UNRATED_MESSAGE } from './CreditReportConstants';
import { formatDateByFormat, DATE_FORMATS } from '../../../../../../utils/dateUtils';

interface CreditRatingItem {
    date: string;
    agency: string;
    ratingShortTerm: string;
    ratingLongTerm: string;
    outlook?: string;
    sourceLink?: string;
}

interface CreditRatingData {
    ratingSummary: string;
    ratingData: CreditRatingItem[];
}

interface CreditReportProps {
    isPDFReport?: boolean;
    creditRating?: CreditRatingData;
}

function CreditReport({ isPDFReport = false, creditRating }: CreditReportProps) {
    const { ratingSummary, ratingData } = creditRating || {};
    const isUnrated = !ratingData || ratingData.length === 0;

    // Function to get cell JSX element with appropriate formatting and styling
    const getRowData = (headerId: string, item: CreditRatingItem, rowIndex: number) => {
        switch (headerId) {
            case HEADER_IDS.DATE:
                return (
                    <div className={clsx(styles.rowItem, styles.dateCell)}>
                        {formatDateByFormat(item?.date, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY)}
                    </div>
                );
            case HEADER_IDS.AGENCY:
                return <div className={`${styles.rowItem} ${styles.agencyCell}`}>{item?.agency}</div>;
            case HEADER_IDS.OUTLOOK:
                return (
                    <div className={styles.rowItem}>
                        {item?.outlook ? (
                            <div className={`${styles.outlookPill} ${styles[item.outlook.toLowerCase()]}`}>{item.outlook}</div>
                        ) : (
                            '-'
                        )}
                    </div>
                );
            case HEADER_IDS.SOURCE_LINK:
                return (
                    <div className={styles.rowItem}>
                        {item.sourceLink ? (
                            <a href={item.sourceLink} target="_blank" rel="noopener noreferrer" className={styles.sourceLink}>
                                <TopRightArrowIcon className={styles.viewIcon} />
                                View
                            </a>
                        ) : (
                            '-'
                        )}
                    </div>
                );
            default:
                return <div className={styles.rowItem}>{item?.[headerId as keyof CreditRatingItem]}</div>;
        }
    };

    const render = (
        <div className={styles.creditReport}>
            <HeaderBodyBox title="Credit Rating" isPDFReport={isPDFReport} headerRight={<AIGeneratedBadge />}>
                <div className={styles.content}>
                    {isUnrated ? (
                        <div className={styles.unratedMessage}>{UNRATED_MESSAGE}</div>
                    ) : (
                        <>
                            {ratingSummary && <div className={styles.summary}>{ratingSummary}</div>}
                            <div className={styles.table}>
                                <div className={styles.tableHeader}>
                                    {headers?.map((header) => (
                                        <div className={clsx(styles.headerItem)} key={header.id}>
                                            {header.label}
                                        </div>
                                    ))}
                                </div>
                                <div className={styles.tableBody}>
                                    {ratingData?.map((item, index) => (
                                        <div className={styles.tableRow} key={index}>
                                            {headers?.map((header) => {
                                                return (
                                                    <React.Fragment key={`${index}-${header.id}`}>
                                                        {getRowData(header.id, item, index)}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </HeaderBodyBox>
        </div>
    );

    if (isPDFReport) {
        return <A4Page sectionName={REPORT_SECTION_NAMES.CREDIT_RATING}>{render}</A4Page>;
    } else {
        return render;
    }
}

export default CreditReport;
